/*
 * Copyright 2026 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';
import {
  EMBER_THEME_NAME,
  emberDarkTheme,
  emberLightTheme,
} from './emberTheme';
import './emberTheme.css';

const THEME_STORAGE_KEY = 'theme';
const DEFAULT_APPLIED_STORAGE_KEY = 'ember-theme-default-applied';

export function applyDefaultDarkTheme() {
  try {
    const storage = window.localStorage;
    if (storage.getItem(DEFAULT_APPLIED_STORAGE_KEY)) {
      return;
    }
    if (!storage.getItem(THEME_STORAGE_KEY)) {
      storage.setItem(THEME_STORAGE_KEY, 'dark');
    }
    storage.setItem(DEFAULT_APPLIED_STORAGE_KEY, '1');
  } catch {
    return;
  }
}

const darkTheme = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider
          theme={emberDarkTheme}
          themeName={EMBER_THEME_NAME}
          children={children}
        />
      ),
    },
  },
});

const lightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider
          theme={emberLightTheme}
          themeName={EMBER_THEME_NAME}
          children={children}
        />
      ),
    },
  },
});

export const appModuleTheme = createFrontendModule({
  pluginId: 'app',
  extensions: [darkTheme, lightTheme],
});
